import { motion } from 'framer-motion';
import moment from "moment";
import { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import Spinner from './components/loader_spinner/Spinner';
import Navbar from './components/Navbar';
import LoaderError from './components/shared/adLoader/LoaderError';
import ErrorBoundary from './context/ErrorBoundary';
import AuthCallback from './features/auth/AuthCallback';
import Home from './features/home/home';
import PaymentInstructions from './features/profile/components/PaymentInstructions';
import { AdminRoute } from './routes/AdminRoute';
import PrivateRoute from './routes/PrivateRoute';
import ForgotPassword from './features/auth/ForgotPassword';
import ResetPassword from './features/auth/ResetPassword';
import EmailVerification from './features/auth/EmailVerification';

// Set French locale globally at the application entry point
moment.locale("fr")

// Loading component
const LoadingFallback = () => {
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setHasTimedOut(true);
    }, 8000); // 8 seconds timeout

    return () => clearTimeout(timeoutId);
  }, []);

  if (hasTimedOut) {
    return <LoaderError />;
  }

  return (
    <div className='flex items-center justify-center min-h-screen'>
      <Spinner />
    </div>
  );
};

// Lazy load components
const Register = lazy(() => import('./features/auth/Register'));
const Login = lazy(() => import('./features/auth/Login'));
const Profile = lazy(() => import('./features/profile/Profile'));
const Sell = lazy(() => import('./features/sell/Sell'));
const PremiumAds = lazy(() => import('./features/premiumAds/PremiumAds'));
const FavoriteAds = lazy(() => import('./features/favorite_ads/FavoriteAds'));
const SingleProduct = lazy(() => import('./features/Single_product/SingleProduct'));
const Chats = lazy(() => import('./features/Chats/Chats'));
const SearchResults = lazy(() => import('./features/seachResults/SearchResults'));
const AdminRegistration = lazy(() => import('./features/admin/verifications/AdminRegistration'));
const AdminDashboard = lazy(() => import('./features/admin/components/AdminDashboard'));
const AdminLogin = lazy(() => import('./features/admin/verifications/AdminLogin'));
const PrivacyPolicy = lazy(() => import('./features/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./features/pages/TermsOfService'));
const AboutFala = lazy(() => import('./features/pages/AboutFala'));
const WeAreHiring = lazy(() => import('./features/pages/WeAreHiring'));
const BillingPolicy = lazy(() => import('./features/pages/BillingPolicy'));
const CopyrightViolationPolicy = lazy(() => import('./features/pages/CopyrightViolationPolicy'));
const SecurityTips = lazy(() => import('./features/pages/SecurityTips'));
const ContactUs = lazy(() => import('./features/pages/ContactUs'));
const FAQ = lazy(() => import('./features/pages/FAQ'));
const BlogListPage = lazy(() => import('./features/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./features/blog/BlogPostPage'));

const App = () => {
  return (
    <ErrorBoundary>
      <Navbar />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Admin authentication routes */}
          <Route path="/login-admin" element={<AdminLogin />} />
          <Route path="/auth/create-admin" element={<AdminRegistration />} />

          {/* Protected routes for both regular users and admins */}
          <Route element={<PrivateRoute />}>
            <Route path='/favorites' element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}><FavoriteAds /></motion.div>} />
            <Route path='/chats' element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}><Chats /></motion.div>} />
            <Route path='/sell' element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}><Sell /></motion.div>} />

            {/* Admin dashboard with additional admin check */}
            <Route path="/admin-dashboard/*" element={
              <AdminRoute>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                  <AdminDashboard />
                </motion.div>
              </AdminRoute>
            } />
          </Route>

          {/* Public routes */}
          <Route path="/premium-ads" element={<PremiumAds />} />
          <Route path="/search/:categoryName?/:subcategoryName?" element={<SearchResults />} />
          <Route path="/search/location/:stateId/:cityId" element={<SearchResults />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/verify-email" element={<EmailVerification />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Home />} />
          <Route path="/:categoryName/:subcategoryName/:adId" element={<SingleProduct />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/payment-instructions" element={<PaymentInstructions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/about-falashop" element={<AboutFala />} />
          <Route path="/we-are-hiring" element={<WeAreHiring />} />
          <Route path="/billing-policy" element={<BillingPolicy />} />
          <Route path="/copyright-violation-policy" element={<CopyrightViolationPolicy />} />
          <Route path="/security-tips" element={<SecurityTips />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

const AppWrapper = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="bg-gray-100 flex flex-col justify-center"
  >
    <App />
  </motion.div>
);

export default AppWrapper;
